import { Component, inject, input, OnInit, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormField, form, maxLength, pattern, required } from '@angular/forms/signals';
import { ETipoDocumento } from '@core/enums/tipoDocumento.enum';
import { IClienteData } from '@core/interfaces/entidades/cliente.entidad';
import { UiButton, UiInput, UiInputMask, UiSelect, UiSelectButton } from '@shared/components/ui';
import { TipoDocumentoPipe } from '../../pipes/tipo-documento.pipe';
import { ClienteApiService } from '@core/services/api/cliente-api.service';
import { ToastService } from '@shared/services/toast.service';
import { buildDiff, normalizarTelefono } from '@shared/utils/functions.utils';
import { FormErrorMessagePipe } from '@shared/pipes/form-error-message.pipe';

@Component({
  selector: 'app-editar-cliente',
  imports: [
    FormField,
    UiSelect,
    UiSelectButton,
    UiInput,
    UiInputMask,
    UiButton,
    TipoDocumentoPipe,
    FormErrorMessagePipe,
  ],
  templateUrl: './editar-cliente.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './editar-cliente.css',
})
export class EditarCliente implements OnInit {
  ngOnInit(): void {
    // Inicializar el formulario con los datos del cliente recibido
    const cli = this.cliente();
    this.formularioCliente().value.set({
      nombre: cli.nombre_completo,
      dniRuc: cli.dniRuc,
      direccion: cli.direccion,
      tipo_documento: cli.tipo_documento || ETipoDocumento.DNI,
    });
    this.numero_celular = cli.numero_celular
      ? normalizarTelefono(cli.numero_celular).slice(3)
      : undefined;
  }
  // servicios
  private clienteApiService = inject(ClienteApiService);
  private toastService = inject(ToastService);
  // datos
  tipoBusqueda = [
    { label: 'DNI', value: ETipoDocumento.DNI },
    { label: 'RUC', value: ETipoDocumento.RUC },
    { label: 'CE', value: ETipoDocumento.CE },
    { label: 'PASAPORTE', value: ETipoDocumento.PASAPORTE },
    {
      label: 'P.T.P',
      value: ETipoDocumento.PERMISO_TEMPORAL_PERMANENCIA,
    },
    {
      label: 'Doc. Extranjero',
      value: ETipoDocumento.DOCUMENTO_IDENTIDAD_EXTRANJERO,
    },
  ];
  ETipoDocumento = ETipoDocumento;
  cliente = input.required<IClienteData>();
  formularioCliente = form(
    signal({
      nombre: '',
      dniRuc: '',
      direccion: '',
      tipo_documento: ETipoDocumento.DNI,
    }),
    (schemaPath) => {
      required(schemaPath.tipo_documento, { message: 'El tipo de documento es obligatorio.' });
      required(schemaPath.nombre, { message: 'El nombre completo es obligatorio.' });
      required(schemaPath.dniRuc, { message: 'El DNI o RUC es obligatorio.' });
      pattern(schemaPath.nombre, /^[a-zA-ZáéíóúÁÉÍÓÚüÜñÑ\s]+$/, {
        message: 'El nombre solo puede contener letras y espacios.',
      });
      maxLength(schemaPath.nombre, 125, {
        message: 'El nombre no puede exceder los 125 caracteres.',
      });
      maxLength(schemaPath.dniRuc, 30, {
        message: 'El DNI o RUC no puede exceder los 30 caracteres.',
      });
    },
  );
  numero_celular?: string;

  cerrar = output<IClienteData | undefined>();
  onCerrar() {
    this.cerrar.emit(undefined);
  }

  private checkInvalidForm(): boolean {
    if (this.formularioCliente().invalid()) {
      this.toastService.show(
        'top-right',
        'error',
        'Error',
        'Por favor, complete los campos obligatorios.',
      );
      this.formularioCliente.tipo_documento().markAsTouched();
      this.formularioCliente.nombre().markAsTouched();
      this.formularioCliente.dniRuc().markAsTouched();
      return true;
    }
    return false;
  }
  onEditar($event: Event) {
    $event.preventDefault();
    if (this.checkInvalidForm()) return;
    const { nombre, dniRuc, direccion, tipo_documento } = this.formularioCliente().controlValue();

    const dataToSave = {
      nombre_completo: nombre.trim(),
      dniRuc: dniRuc.trim(),
      direccion: direccion.trim(),
      tipo_documento,
      numero_celular: normalizarTelefono(this.numero_celular || ''),
    };
    const rq: Partial<IClienteData> = buildDiff(this.cliente(), dataToSave);
    if (Object.keys(rq).length === 0) {
      this.toastService.show('top-right', 'info', 'Sin cambios', 'No se han realizado cambios.');
      return;
    }
    this.clienteApiService.editar(this.cliente().id, rq).subscribe({
      next: (data) => {
        this.cerrar.emit(data);
        this.toastService.show('top-right', 'success', 'Éxito', 'Cliente editado correctamente.');
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message || 'Error al editar el cliente',
        );
      },
    });
  }
}
